import { parseCalculatorUserOverlayDocumentV4 } from "../calculator-user-layer-v4-contract.js";
import {
  WORKSPACE_DOCUMENT_VERSION,
  cloneValue,
  defaultClock,
  defaultCreateId,
  detachedSource,
  exactFields,
  frozenClone,
  isoTimestamp,
  opaqueId,
  providerFailure,
} from "./preset-internals.js";

export const LEGACY_CALCULATOR_OVERLAY_V4_STORAGE_KEY =
  "fishystuff.user-overlays.v4";
export const CALCULATOR_OVERLAY_V4_COLLECTION_KEY = "calculator-overlays-v4";
export const CALCULATOR_OVERLAY_V4_MIGRATION_ID =
  "legacy-calculator-overlay-v4";
export const CALCULATOR_OVERLAY_V4_MIGRATION_RECEIPT_VERSION = 1;

const RECEIPT_FIELDS = Object.freeze([
  "id",
  "importedAt",
  "outcome",
  "receiptVersion",
  "sourceKey",
  "sourcePresent",
]);
const RECEIPT_OUTCOMES = new Set([
  "current-won",
  "imported",
  "missing",
  "quarantined",
]);

function migrationReceipt({ clock, outcome, sourcePresent }) {
  return {
    receiptVersion: CALCULATOR_OVERLAY_V4_MIGRATION_RECEIPT_VERSION,
    id: CALCULATOR_OVERLAY_V4_MIGRATION_ID,
    sourceKey: LEGACY_CALCULATOR_OVERLAY_V4_STORAGE_KEY,
    sourcePresent,
    outcome,
    importedAt: isoTimestamp(clock(), "Calculator overlay migration acknowledgement time"),
  };
}

export function decodeCalculatorOverlayV4MigrationReceipt(value) {
  if (
    !exactFields(value, RECEIPT_FIELDS)
    || value.receiptVersion !== CALCULATOR_OVERLAY_V4_MIGRATION_RECEIPT_VERSION
    || value.id !== CALCULATOR_OVERLAY_V4_MIGRATION_ID
    || value.sourceKey !== LEGACY_CALCULATOR_OVERLAY_V4_STORAGE_KEY
    || typeof value.sourcePresent !== "boolean"
    || !RECEIPT_OUTCOMES.has(value.outcome)
    || (value.sourcePresent === (value.outcome === "missing"))
  ) {
    throw new TypeError("calculator-overlay-v4-migration-receipt-invalid");
  }
  return {
    receiptVersion: CALCULATOR_OVERLAY_V4_MIGRATION_RECEIPT_VERSION,
    id: CALCULATOR_OVERLAY_V4_MIGRATION_ID,
    sourceKey: LEGACY_CALCULATOR_OVERLAY_V4_STORAGE_KEY,
    sourcePresent: value.sourcePresent,
    outcome: value.outcome,
    importedAt: isoTimestamp(
      value.importedAt,
      "Calculator overlay migration acknowledgement time",
    ),
  };
}

function quarantineRow({ clock, id, rawBytes, reason }) {
  return {
    id,
    collectionKey: CALCULATOR_OVERLAY_V4_COLLECTION_KEY,
    kind: CALCULATOR_OVERLAY_V4_MIGRATION_ID,
    reason,
    path: "$",
    rawBytes,
    quarantinedAt: isoTimestamp(clock(), "Calculator overlay quarantine time"),
  };
}

function invalidLegacyReason(rawBytes) {
  let parsed;
  try {
    parsed = JSON.parse(rawBytes);
  } catch (_error) {
    return "malformed-json";
  }
  return Number.isSafeInteger(parsed?.documentVersion)
    && parsed.documentVersion > 4
    ? "unsupported-version"
    : "invalid-document";
}

function workspaceRecord(document, { clock, createId }) {
  const at = isoTimestamp(clock(), "Calculator overlay migration time");
  const changeId = opaqueId(
    createId("workspace_change"),
    "Calculator overlay workspace change ID",
  );
  const revisionId = opaqueId(
    createId("workspace_revision"),
    "Calculator overlay workspace revision ID",
  );
  return {
    documentVersion: WORKSPACE_DOCUMENT_VERSION,
    collectionKey: CALCULATOR_OVERLAY_V4_COLLECTION_KEY,
    storeRevision: opaqueId(
      createId("workspace_store_revision"),
      "Calculator overlay workspace store revision",
    ),
    headRevisionId: revisionId,
    headChangeId: changeId,
    revisions: [{
      revisionId,
      changeId,
      parentRevisionId: null,
      supersedesRevisionId: null,
      actionId: opaqueId(
        createId("preset_action"),
        "Calculator overlay import action ID",
      ),
      actionKind: "legacy-overlay-v4-import",
      at,
      basedOn: detachedSource(),
      compacted: false,
      payload: cloneValue(document),
    }],
    lastVisitedChildren: [],
    createdAt: at,
    updatedAt: at,
  };
}

export function buildCalculatorOverlayV4Import(rawBytes, {
  clock = defaultClock,
  createId = defaultCreateId,
} = {}) {
  if (typeof rawBytes !== "string") {
    throw new TypeError("Calculator overlay migration input must be original text bytes.");
  }
  try {
    const document = parseCalculatorUserOverlayDocumentV4(JSON.parse(rawBytes));
    return {
      workspaceRecords: [workspaceRecord(document, { clock, createId })],
      quarantine: [],
      outcome: "imported",
    };
  } catch (_error) {
    return {
      workspaceRecords: [],
      quarantine: [quarantineRow({
        clock,
        id: "legacy_calculator_overlay_v4_invalid",
        rawBytes,
        reason: invalidLegacyReason(rawBytes),
      })],
      outcome: "quarantined",
    };
  }
}

function cleanupAcknowledgedSource(storage, sourcePresent, receipt) {
  if (!sourcePresent) {
    return null;
  }
  try {
    if (
      !storage
      || typeof storage.getItem !== "function"
      || typeof storage.removeItem !== "function"
    ) {
      throw new TypeError("Legacy calculator overlay storage is unavailable.");
    }
    storage.removeItem(LEGACY_CALCULATOR_OVERLAY_V4_STORAGE_KEY);
    if (storage.getItem(LEGACY_CALCULATOR_OVERLAY_V4_STORAGE_KEY) !== null) {
      throw new Error("Legacy calculator overlay cleanup was not acknowledged.");
    }
    return null;
  } catch (_error) {
    return frozenClone({
      kind: "unavailable",
      operation: "remove-legacy-calculator-overlay-v4",
      reason: "legacy-storage-remove-failed",
      receipt,
    });
  }
}

export async function migrateLegacyCalculatorOverlayV4Storage({
  provider,
  storage,
  clock = defaultClock,
  createId = defaultCreateId,
} = {}) {
  if (
    !provider
    || typeof provider.migrationReceipt !== "function"
    || typeof provider.atomicLegacyImport !== "function"
  ) {
    throw new TypeError(
      "Calculator overlay migration requires an IndexedDB preset provider.",
    );
  }

  let existingReceipt;
  try {
    existingReceipt = await provider.migrationReceipt(
      CALCULATOR_OVERLAY_V4_MIGRATION_ID,
    );
  } catch (error) {
    return frozenClone(providerFailure("read-calculator-overlay-v4-migration", error));
  }
  if (existingReceipt !== null && existingReceipt !== undefined) {
    let receipt;
    try {
      receipt = decodeCalculatorOverlayV4MigrationReceipt(existingReceipt);
    } catch (error) {
      return frozenClone({
        kind: "invalid",
        boundary: "calculator-overlay-v4-migration-receipt",
        reason: error.message,
      });
    }
    const cleanupFailure = cleanupAcknowledgedSource(
      storage,
      receipt.sourcePresent,
      receipt,
    );
    return cleanupFailure ?? frozenClone({
      kind: "acknowledged",
      imported: false,
      receipt,
    });
  }

  let rawBytes;
  try {
    if (!storage || typeof storage.getItem !== "function") {
      throw new TypeError("Legacy calculator overlay storage is unavailable.");
    }
    rawBytes = storage.getItem(LEGACY_CALCULATOR_OVERLAY_V4_STORAGE_KEY);
  } catch (_error) {
    return frozenClone({
      kind: "unavailable",
      operation: "read-legacy-calculator-overlay-v4",
      reason: "legacy-storage-read-failed",
    });
  }

  const sourcePresent = rawBytes !== null;
  const plan = sourcePresent
    ? buildCalculatorOverlayV4Import(rawBytes, { clock, createId })
    : { workspaceRecords: [], quarantine: [], outcome: "missing" };
  const receipt = migrationReceipt({
    clock,
    outcome: plan.outcome,
    sourcePresent,
  });
  const collisionReceipt = sourcePresent && plan.workspaceRecords.length > 0
    ? migrationReceipt({ clock, outcome: "current-won", sourcePresent })
    : null;
  let result;
  try {
    result = await provider.atomicLegacyImport({
      workspaceRecords: plan.workspaceRecords,
      savedRecords: [],
      quarantine: plan.quarantine,
      collisionQuarantine: sourcePresent && plan.workspaceRecords.length > 0
        ? [quarantineRow({
            clock,
            id: "legacy_calculator_overlay_v4_target_collision",
            rawBytes,
            reason: "target-current-already-exists",
        })]
        : [],
      collisionReceipt,
      receipt,
    });
  } catch (error) {
    return frozenClone(providerFailure("write-calculator-overlay-v4-migration", error));
  }

  let acknowledgedReceipt;
  try {
    acknowledgedReceipt = decodeCalculatorOverlayV4MigrationReceipt(result?.receipt);
  } catch (error) {
    return frozenClone({
      kind: "invalid",
      boundary: "calculator-overlay-v4-migration-receipt",
      reason: error.message,
    });
  }
  const cleanupFailure = cleanupAcknowledgedSource(
    storage,
    acknowledgedReceipt.sourcePresent,
    acknowledgedReceipt,
  );
  if (cleanupFailure) {
    return cleanupFailure;
  }
  return frozenClone({
    kind: "acknowledged",
    imported: result.imported === true && acknowledgedReceipt.outcome === "imported",
    receipt: acknowledgedReceipt,
  });
}
